import ApiServiceNode from '../service/ApiServiceNode';
import logger from '../utils/logger';



export const userService = {
    /**
     * Get users list with filters and pagination
     * @param {Object} params - Query parameters (page, limit, search, office_id, department_id, job_status, termination_reason, is_active, sortBy, sortOrder)
     * @returns {Promise} Users list with pagination
     */
    async getUsers({
        page = 1,
        limit = 20,
        search = '',
        office_id = null,
        department_id = null,
        job_status = null,
        termination_reason = null,
        is_active = true,
        include_joining = false,
        missing_probation_expiry = null,
        sortBy = null,
        sortOrder = null
    }) {
        try {
            const params = { page, limit };

            if (search) params.search = search;
            if (office_id) params.office_id = office_id;
            if (department_id) params.department_id = department_id;
            if (job_status) params.job_status = job_status;
            if (termination_reason) params.termination_reason = termination_reason;
            if (is_active !== null) params.is_active = is_active;
            if (include_joining) params.include_joining = true;
            if (missing_probation_expiry) params.missing_probation_expiry = true;
            if (sortBy) params.sortBy = sortBy;
            if (sortOrder) params.sortOrder = sortOrder;

            const response = await ApiServiceNode.get('/settings/users', { params });
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching users:', error);
            throw error;
        }
    },

    /**
     * Get single user by ID
     * @param {Number} id - User ID
     * @returns {Promise} User object
     */
    async getUser(id) {
        try {
            const response = await ApiServiceNode.get(`/settings/users/${id}`);
            return response.data.data?.user || response.data.user || response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching user:', error);
            throw error;
        }
    },

    /**
     * Create new user
     * @param {Object} data - User data
     * @returns {Promise} Created user
     */
    async createUser(data) {
        try {
            const response = await ApiServiceNode.post('/settings/users', data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error creating user:', error);
            throw error;
        }
    },

    /**
     * Update user
     * @param {Number} id - User ID
     * @param {Object} data - Updated user data
     * @returns {Promise} Updated user
     */
    async updateUser(id, data) {
        try {
            const response = await ApiServiceNode.put(`/settings/users/${id}`, data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error updating user:', error);
            throw error;
        }
    },

    /**
     * Sync manager access flag from current active direct reports
     * @param {Number} id - User ID
     * @returns {Promise} Sync result
     */
    async syncManagerStatus(id) {
        try {
            const response = await ApiServiceNode.post(`/settings/users/${id}/sync-manager-status`);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error syncing manager status:', error);
            throw error;
        }
    },

    async sendExitFeedbackTestEmail(id) {
        try {
            const response = await ApiServiceNode.post(`/settings/users/${id}/exit-feedback/test-email`);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error sending exit feedback test email:', error);
            throw error;
        }
    },

    /**
     * Get active users whose direct reports and manager access flag are out of sync
     * @param {Object} params - Query parameters (limit)
     * @returns {Promise} Users needing manager access sync
     */
    async getManagerAccessDrift({ limit = 20 } = {}) {
        try {
            const response = await ApiServiceNode.get('/settings/users/manager-access-drift', {
                params: { limit }
            });
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching manager access drift:', error);
            throw error;
        }
    },

    /**
     * Deactivate user
     * @param {Number} id - User ID
     * @returns {Promise} Response
     */
    async deactivateUser(id, options = {}) {
        try {
            const payload = { isActive: false };
            if (options.replace_manager_id) {
                payload.replace_manager_id = options.replace_manager_id;
            }

            const response = await ApiServiceNode.put(`/settings/users/${id}/active`, payload);
            return response.data;
        } catch (error) {
            logger.error('Error deactivating user:', error);
            throw error;
        }
    },

    /**
     * Reactivate user
     * @param {Number} id - User ID
     * @returns {Promise} Response
     */
    async activateUser(id) {
        try {
            const response = await ApiServiceNode.put(`/settings/users/${id}/active`, { isActive: true });
            return response.data;
        } catch (error) {
            logger.error('Error activating user:', error);
            throw error;
        }
    },

    /**
     * Delete user
     * @param {Number} id - User ID
     * @returns {Promise} Response
     */
    async deleteUser(id) {
        try {
            const response = await ApiServiceNode.delete(`/settings/users/${id}`);
            return response.data;
        } catch (error) {
            logger.error('Error deleting user:', error);
            throw error;
        }
    },

    /**
     * Get dropdowns for user form (offices, departments, managers, roles)
     * @returns {Promise} Dropdowns data
     */
    async getDropdowns() {
        try {
            const response = await ApiServiceNode.get('/settings/users/dropdowns');
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching dropdowns:', error);
            throw error;
        }
    },

    // ========================================================================
    // COST RATE CRUD
    // ========================================================================

    /**
     * Create cost rate for user
     * @param {Number} userId - User ID
     * @param {Object} data - Cost rate data (rate, valid_at)
     * @returns {Promise} Created cost rate
     */
    async createCostRate(userId, data) {
        try {
            const response = await ApiServiceNode.post(`/settings/users/${userId}/cost-rates`, data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error creating cost rate:', error);
            throw error;
        }
    },

    /**
     * Update cost rate
     * @param {Number} userId - User ID
     * @param {Number} id - Cost rate ID
     * @param {Object} data - Cost rate data (rate, valid_at)
     * @returns {Promise} Updated cost rate
     */
    async updateCostRate(userId, id, data) {
        try {
            const response = await ApiServiceNode.put(`/settings/users/${userId}/cost-rates/${id}`, data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error updating cost rate:', error);
            throw error;
        }
    },

    /**
     * Delete cost rate
     * @param {Number} userId - User ID
     * @param {Number} id - Cost rate ID
     * @returns {Promise} Delete response
     */
    async deleteCostRate(userId, id) {
        try {
            const response = await ApiServiceNode.delete(`/settings/users/${userId}/cost-rates/${id}`);
            return response.data;
        } catch (error) {
            logger.error('Error deleting cost rate:', error);
            throw error;
        }
    },

    // ========================================================================
    // SALARY CRUD
    // ========================================================================

    /**
     * Create salary for user
     * @param {Number} userId - User ID
     * @param {Object} data - Salary data (amount, hourly_rate, valid_at, office_id)
     * @returns {Promise} Created salary
     */
    async createSalary(userId, data) {
        try {
            const response = await ApiServiceNode.post(`/settings/users/${userId}/salaries`, data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error creating salary:', error);
            throw error;
        }
    },

    /**
     * Update salary
     * @param {Number} userId - User ID
     * @param {Number} id - Salary ID
     * @param {Object} data - Salary data (amount, hourly_rate, valid_at, office_id)
     * @returns {Promise} Updated salary
     */
    async updateSalary(userId, id, data) {
        try {
            const response = await ApiServiceNode.put(`/settings/users/${userId}/salaries/${id}`, data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error updating salary:', error);
            throw error;
        }
    },

    /**
     * Delete salary
     * @param {Number} userId - User ID
     * @param {Number} id - Salary ID
     * @returns {Promise} Delete response
     */
    async deleteSalary(userId, id) {
        try {
            const response = await ApiServiceNode.delete(`/settings/users/${userId}/salaries/${id}`);
            return response.data;
        } catch (error) {
            logger.error('Error deleting salary:', error);
            throw error;
        }
    },

    // ========================================================================
    // Title History CRUD
    // ========================================================================

    async createTitle(userId, data) {
        try {
            const response = await ApiServiceNode.post(`/settings/users/${userId}/titles`, data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error creating title:', error);
            throw error;
        }
    },

    async updateTitle(userId, titleId, data) {
        try {
            const response = await ApiServiceNode.put(`/settings/users/${userId}/titles/${titleId}`, data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error updating title:', error);
            throw error;
        }
    },

    async deleteTitle(userId, titleId) {
        try {
            const response = await ApiServiceNode.delete(`/settings/users/${userId}/titles/${titleId}`);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error deleting title:', error);
            throw error;
        }
    },

    // ========================================================================
    // EXPORT REPORTS
    // ========================================================================

    /**
     * Export report as Excel file
     * @param {string} reportType - 'birthday-listing' | 'work-anniversary' | 'confirmation-due' | 'personal-info'
     * @param {string} target - 'local' | 'google'
     * @param {Object} extraParams - Additional query parameters (e.g. { status: 'active' })
     * @returns {Promise} For google: JSON with fileUrl. For local: blob response.
     */
    async exportReport(reportType, target = 'local', extraParams = {}) {
        try {
            if (target === 'google') {
                const response = await ApiServiceNode.get(`/settings/users/exports/${reportType}`, {
                    params: { _target: 'google', ...extraParams }
                });
                return response.data.data || response.data;
            } else {
                const response = await ApiServiceNode.get(`/settings/users/exports/${reportType}`, {
                    params: { _target: 'local', ...extraParams },
                    responseType: 'blob'
                });
                return response.data;
            }
        } catch (error) {
            logger.error(`Error exporting ${reportType}:`, error);
            throw error;
        }
    }
};
